import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { overrideOrganizationHeader } from '@cast/api-client';

const RBAC_ROLE_BINDING_QUERY_KEY = 'rbac-role-binding';

const getRbacRoleBindingQueryKey = (params: {
  organizationId: string;
  roleBindingId: string;
}) =>
  [
    RBAC_ROLE_BINDING_QUERY_KEY,
    params.organizationId,
    params.roleBindingId,
  ] as const;

const getRbacRoleBindingQueryOptions = ({
  organizationId,
  roleBindingId,
  enabled = true,
}: {
  organizationId: string;
  roleBindingId: string;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getRbacRoleBindingQueryKey({ organizationId, roleBindingId }),
    queryFn: async () => {
      const { data } = await apiClient.rbac.getRoleBinding(
        {
          organizationId,
          id: roleBindingId,
        },
        {
          headers: overrideOrganizationHeader(organizationId),
        }
      );
      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!roleBindingId, enabled),
  });

type UseRbacRoleBindingQueryProps = {
  roleBindingId: string;
  organizationId?: string;
  enabled?: boolean;
};

export const useRbacRoleBindingQuery = ({
  roleBindingId,
  organizationId: organizationIdOverride,
  enabled = true,
}: UseRbacRoleBindingQueryProps) => {
  const { currentOrganization } = useOrganizations();
  // "||" (not "??") so an empty-string override falls back to the session
  // org instead of disabling the query (API may return "" for unset ids).
  const organizationId = organizationIdOverride || currentOrganization?.id;

  return useQuery(
    getRbacRoleBindingQueryOptions({
      organizationId: organizationId ?? '',
      roleBindingId,
      enabled: enabled && !!organizationId,
    })
  );
};
